import { z } from 'zod';

/**
 * Feature codes: what an account's phones dial to do something themselves.
 *
 * A list (docs/31): each row is a code, the action it takes and, where the action needs one, a
 * target. Several codes can take the same action, and a code can be switched off without
 * losing it. Every account starts with the ten codes it has always had (migration 0071), so
 * nothing anybody dials changes; the defaults are the widely recognised ones, because somebody
 * who has used a phone system before already knows *78.
 *
 * The switch builds its plan from the same rows (agent fsxml.ts, featureListXml).
 */

/** The ten every account has had since the codes became its own (docs/26 E1). */
export const CORE_FEATURE_CODE_ACTIONS = [
  'dnd_on', 'dnd_off', 'fwd_on', 'fwd_off', 'hotdesk_login', 'hotdesk_logout',
  'voicemail_own', 'voicemail_other', 'queue_pause', 'queue_unpause',
] as const;
export type CoreFeatureCodeAction = (typeof CORE_FEATURE_CODE_ACTIONS)[number];

export const FEATURE_CODE_ACTIONS = [
  ...CORE_FEATURE_CODE_ACTIONS,
  'dnd_toggle', 'voicemail_leave', 'call_target', 'speed_dial', 'withhold',
] as const;
export type FeatureCodeAction = (typeof FEATURE_CODE_ACTIONS)[number];
export const FeatureCodeActionSchema = z.enum(FEATURE_CODE_ACTIONS);

/** What a code can act on or go to. */
export const FEATURE_CODE_TARGET_KINDS = [
  'extension', 'ring_group', 'queue', 'ivr', 'conference', 'parking', 'number',
] as const;
export type FeatureCodeTargetKind = (typeof FEATURE_CODE_TARGET_KINDS)[number];

/** The places a call can be sent, for the Call action. */
export const FEATURE_CODE_DESTINATIONS: readonly FeatureCodeTargetKind[] = [
  'extension', 'ring_group', 'queue', 'ivr', 'conference', 'parking',
];

/**
 * What an action takes as its target.
 * - `none`: nothing; it acts on the phone that dials it, or asks.
 * - `seat`: the phone that dials it, or a named extension (a supervisor's code).
 * - `destination`: where the call goes, which it must have.
 * - `number`: an outside number, which it must have.
 */
export type FeatureCodeTargetRule = 'none' | 'seat' | 'destination' | 'number';

export interface FeatureCodeInfo {
  action: FeatureCodeAction;
  group: string;
  label: string;
  /** What is dialled after the code, for the codes that take something. */
  then: string | null;
  target: FeatureCodeTargetRule;
  /** Offered when a row is added for this action. Null: the next free code. */
  suggested: string | null;
}

/** In the order a person reads them, grouped by what they are for. */
export const FEATURE_CODE_CATALOGUE: readonly FeatureCodeInfo[] = [
  { action: 'dnd_on', group: 'Do not disturb', label: 'Turn on', then: null, target: 'seat', suggested: '*78' },
  { action: 'dnd_off', group: 'Do not disturb', label: 'Turn off', then: null, target: 'seat', suggested: '*79' },
  { action: 'dnd_toggle', group: 'Do not disturb', label: 'Turn on or off', then: null, target: 'seat', suggested: '*76' },
  { action: 'fwd_on', group: 'Forwarding', label: 'Forward every call', then: 'the extension to forward to', target: 'seat', suggested: '*72' },
  { action: 'fwd_off', group: 'Forwarding', label: 'Stop forwarding', then: null, target: 'seat', suggested: '*73' },
  { action: 'hotdesk_login', group: 'Hot desking', label: 'Sit down at a shared desk', then: 'extension, *, PIN', target: 'none', suggested: '*31' },
  { action: 'hotdesk_logout', group: 'Hot desking', label: 'Leave the desk', then: null, target: 'none', suggested: '*32' },
  { action: 'voicemail_own', group: 'Voicemail', label: 'Own mailbox', then: null, target: 'none', suggested: '*97' },
  { action: 'voicemail_other', group: 'Voicemail', label: 'Any mailbox, asked for', then: null, target: 'none', suggested: '*98' },
  { action: 'voicemail_leave', group: 'Voicemail', label: 'Leave a message for', then: 'the extension', target: 'none', suggested: '*99' },
  { action: 'queue_pause', group: 'Queues', label: 'Pause in every queue', then: null, target: 'seat', suggested: '*46' },
  { action: 'queue_unpause', group: 'Queues', label: 'Take calls again', then: null, target: 'seat', suggested: '*47' },
  { action: 'call_target', group: 'Calls', label: 'Call', then: null, target: 'destination', suggested: null },
  { action: 'speed_dial', group: 'Calls', label: 'Speed dial', then: null, target: 'number', suggested: null },
  { action: 'withhold', group: 'Calls', label: 'Withhold my number on one outside call', then: 'the number to call', target: 'none', suggested: '*67' },
];

export const featureCodeInfo = (action: FeatureCodeAction): FeatureCodeInfo =>
  FEATURE_CODE_CATALOGUE.find((c) => c.action === action)!;

/**
 * What an account dials until somebody chooses otherwise. Mirrored in migration 0071 (every
 * account's first rows) and in apps/agent/src/fsxml.ts, for a node that has been sent no list.
 *
 * The unpause default matters most: mod_callcenter pauses an agent by itself after three
 * unanswered queue calls in a row, and an account with no unpause code would leave that agent
 * no way back.
 */
export const DEFAULT_FEATURE_CODES: Readonly<Record<CoreFeatureCodeAction, string>> = {
  dnd_on: '*78', dnd_off: '*79',
  fwd_on: '*72', fwd_off: '*73',
  hotdesk_login: '*31', hotdesk_logout: '*32',
  voicemail_own: '*97', voicemail_other: '*98',
  queue_pause: '*46', queue_unpause: '*47',
};

/** Codes followed by digits: *721002 forwards to 1002, *311001*4729 sits down. */
export const TAKES_DIGITS: ReadonlySet<FeatureCodeAction> = new Set<FeatureCodeAction>([
  'fwd_on', 'hotdesk_login', 'voicemail_leave', 'withhold',
]);

/** A star and one to eight digits: dialable on any handset, never an extension. */
export const FEATURE_CODE_PATTERN = /^\*[0-9]{1,8}$/;

/** An outside number a speed dial may call: digits, a leading + allowed. */
export const SPEED_DIAL_PATTERN = /^\+?[0-9]{3,20}$/;

/** Why a single code is not usable as written, or null. */
export function featureCodeProblem(code: string): string | null {
  return FEATURE_CODE_PATTERN.test(code)
    ? null
    : 'Use a star followed by one to eight digits, for example *46.';
}

/** One row of the list, as saved. */
export const FeatureCodeRowSchema = z.object({
  code: z.string().trim(),
  action: FeatureCodeActionSchema,
  target_kind: z.enum(FEATURE_CODE_TARGET_KINDS).nullable().default(null),
  /** The extension, hunt list, queue, IVR, conference room or parking lot. */
  target_id: z.string().uuid().nullable().default(null),
  /** A speed dial's outside number. */
  target_number: z.string().trim().nullable().default(null),
  /** What a phone key for it says, when a template gives it one. */
  label: z.string().trim().max(24).nullable().default(null),
  enabled: z.boolean().default(true),
});
export type FeatureCodeRow = z.infer<typeof FeatureCodeRowSchema>;
export type FeatureCodeRowInput = z.input<typeof FeatureCodeRowSchema>;

export type FeatureCodeRowProblems = Partial<Record<'code' | 'target', string>>;

const describe = (r: Pick<FeatureCodeRow, 'action'>): string => {
  const info = featureCodeInfo(r.action);
  return `${info.group}, ${info.label.toLowerCase()}`;
};

/** Why a row's target does not suit its action, or null. */
function targetProblem(r: FeatureCodeRow): string | null {
  const rule = featureCodeInfo(r.action).target;
  const hasObject = r.target_id !== null;
  const hasNumber = r.target_number !== null && r.target_number !== '';
  switch (rule) {
    case 'none':
      return r.target_kind || hasObject || hasNumber ? 'This action takes no target.' : null;
    case 'seat':
      if (r.target_kind === null) return hasObject || hasNumber ? 'Choose an extension, or none.' : null;
      return r.target_kind === 'extension' && hasObject && !hasNumber ? null : 'Choose an extension, or none.';
    case 'destination':
      return r.target_kind && FEATURE_CODE_DESTINATIONS.includes(r.target_kind) && hasObject && !hasNumber
        ? null : 'Choose where the call goes.';
    case 'number':
      if (r.target_kind !== 'number' || hasObject || !hasNumber) return 'Enter the number to dial.';
      return SPEED_DIAL_PATTERN.test(r.target_number!) ? null : 'Use digits only, with a + in front if you like.';
  }
}

/**
 * Why each row does not work, by its place in the list.
 *
 * The rules are the ones the fixed table had, now across rows. No two rows share a code, even
 * one switched off, which keeps the code free for it. And nothing may START with a code that
 * takes digits after it, because the switch reads whatever follows as those digits: with
 * forwarding on *72, a pause code of *7212 is "forward my calls to 12". A code that is merely a
 * prefix OF another is fine: the switch matches whole strings.
 */
export function featureCodeRowProblems(rows: readonly FeatureCodeRow[]): FeatureCodeRowProblems[] {
  return rows.map((r, i) => {
    const out: FeatureCodeRowProblems = {};
    const target = targetProblem(r);
    if (target) out.target = target;
    const problem = featureCodeProblem(r.code);
    if (problem) { out.code = problem; return out; }
    for (let j = 0; j < rows.length; j++) {
      const other = rows[j]!;
      if (i === j || featureCodeProblem(other.code)) continue;
      if (other.code === r.code) {
        out.code = `${r.code} is also ${describe(other)}.`;
        break;
      }
      if (TAKES_DIGITS.has(other.action) && r.code.startsWith(other.code)) {
        out.code = `Starts with ${other.code} (${describe(other)}), which reads what follows as digits.`;
        break;
      }
    }
    return out;
  });
}

/**
 * The whole list, every time. Saving it together is what makes the clash rules checkable: a
 * change to one row can only clash with the others as they will be, not as they were.
 */
export const FeatureCodeListUpdateSchema = z.object({
  codes: z.array(FeatureCodeRowSchema).max(200),
}).strict().superRefine((v, issue) => {
  featureCodeRowProblems(v.codes).forEach((p, i) => {
    for (const field of ['code', 'target'] as const) {
      const message = p[field];
      if (message) {
        issue.addIssue({
          code: z.ZodIssueCode.custom, message,
          path: ['codes', i, field === 'target' ? 'target_kind' : 'code'],
        });
      }
    }
  });
});
export type FeatureCodeListUpdate = z.infer<typeof FeatureCodeListUpdateSchema>;

/** A saved row, with what its target is called. */
export interface FeatureCodeListRow extends FeatureCodeRow {
  id: string;
  /** "1002 Tim", "Sales queue", or null for none. */
  target_name: string | null;
  /** The extension or object it names is gone: the code does nothing until it is changed. */
  target_missing: boolean;
}

/** The ten as rows, as every account starts. */
export const DEFAULT_FEATURE_CODE_LIST: readonly FeatureCodeRow[] = CORE_FEATURE_CODE_ACTIONS.map((action) => ({
  code: DEFAULT_FEATURE_CODES[action], action,
  target_kind: null, target_id: null, target_number: null, label: null, enabled: true,
}));

/**
 * The code for each of the ten, as a phone key or a hint dials it: the first code switched on
 * that acts on the dialling phone, shortest first. Null where the account has none.
 */
export function seatCodes(
  rows: ReadonlyArray<Pick<FeatureCodeRow, 'code' | 'action' | 'target_kind' | 'enabled'>>,
): Record<CoreFeatureCodeAction, string | null> {
  const out = Object.fromEntries(CORE_FEATURE_CODE_ACTIONS.map((a) => [a, null])) as
    Record<CoreFeatureCodeAction, string | null>;
  const ordered = [...rows].sort((a, b) => a.code.length - b.code.length || a.code.localeCompare(b.code));
  for (const r of ordered) {
    if (!r.enabled || r.target_kind !== null) continue;
    const action = r.action as CoreFeatureCodeAction;
    if (action in out && out[action] === null) out[action] = r.code;
  }
  return out;
}

/** What an account dials now. */
export interface FeatureCodes {
  /** Each of the ten, as a phone key or a hint dials it; null where the account has none. */
  codes: Record<CoreFeatureCodeAction, string | null>;
  list: FeatureCodeListRow[];
  defaults: Readonly<Record<CoreFeatureCodeAction, string>>;
}
